import { useSearchParams } from 'react-router-dom'
import { useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import { useEffect, useMemo, useState } from 'react'

import { InboxSubnavs } from 'app/components/inbox/InboxSubnavs'
import { INBOX_THREADS_QUERY } from 'app/components/inbox/graphql'
import { hasInboxProduct, useInboxLocations } from 'app/components/inbox/useInboxLocations'
import { useSubnav } from 'app/components/SideBarContext'
import { useInboxController } from 'app/hooks/useInboxController'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { useSettings } from 'app/hooks/useSettings'
import { gqlFetchList, gqlQuery } from 'app/lib/graphqlClient'
import { resolveInboxLocationId } from 'lib/inboxLocationResolution'
import type { LocationJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import { useHandleSearchParams } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

// Server-backed, alongside `inbox-sort` and `inbox-filter`, so the three inbox
// controls all persist over the same span.
const INBOX_LOCATION_SETTING_KEY = `inbox-location`

const INBOX_LOCATION_QUERY = graphql(`
  query InboxLocation($id: ID!) {
    location(id: $id) {
      id
      name
      primaryTrackingNumber
      products
    }
  }
`)

export type InboxPageData = {
  locationId: string
  filter: string
  query: string
  threadId: string
  location?: LocationJson
}

export type UseInboxPageReturn = {
  data: InboxPageData
  searchParams: ReturnType<typeof useSearchParams>[0]
  handleSearchParams: ReturnType<typeof useHandleSearchParams>
  controller: ReturnType<typeof useInboxController>
  location: LocationJson | undefined
  effectiveUnreadLocations: Record<string, number>
  /** Inbox-entitled locations for this workspace; undefined while they load. */
  inboxLocations: LocationJson[] | undefined
  isMobile: boolean
  // The conversation-list subnav content. On desktop it's pushed into the
  // sidebar rail via `useSubnav`; on mobile it's rendered as a first-class
  // full-width surface in `main` (master-detail), so the page component needs it.
  subnav: ReturnType<typeof InboxSubnavs>
}

async function fetchInboxLocation(locationId: string): Promise<LocationJson | undefined> {
  const { data, error } = await gqlQuery(INBOX_LOCATION_QUERY, { id: locationId })
  if (error) throw new Error(error.message)
  // The page passes LocationJson around; this selection is the four fields it reads.
  return (data?.location ?? undefined) as LocationJson | undefined
}

/**
 * Skips the opt-out suppression the displayed badges apply: this only orders a
 * cold start, and a second round trip to refine an ordering nobody sees would
 * slow the one load where the user waits on an empty screen.
 */
async function fetchUnreadThreadsByLocation(): Promise<Record<string, number>> {
  const { hits } = await gqlFetchList<MessageJson>(
    `messages`,
    { search: `NOT read:true AND NOT archived:true`, pagination: { limit: 5000 } },
    `id locationId threadId sentByUserId isAgent`,
    { requestPolicy: `network-only` },
  )
  // Threads, not messages: the inbox treats a thread as the unit of read state.
  const threadsByLocation: Record<string, Set<string>> = {}
  for (const message of hits) {
    // Only inbound (customer-side) messages count as unread.
    if (message.sentByUserId || message.isAgent) continue
    if (!message.locationId || !message.threadId) continue
    if (!threadsByLocation[message.locationId]) threadsByLocation[message.locationId] = new Set()
    threadsByLocation[message.locationId].add(message.threadId)
  }
  const counts: Record<string, number> = {}
  for (const [locationId, threads] of Object.entries(threadsByLocation)) {
    counts[locationId] = threads.size
  }
  return counts
}

export function useInboxPage(): UseInboxPageReturn {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const isMobile = useIsMobile()

  const locationId = searchParams.get(`locationId`) ?? ``
  const filter = searchParams.get(`filter`) ?? ``
  const query = searchParams.get(`query`) ?? ``
  const tabThreadId = searchParams.get(`tab`) ?? ``

  const [location, setLocation] = useState<LocationJson | undefined>(undefined)
  // Shares the urql document with the subnav's picker, so the pair costs one request.
  const { locations: inboxLocations, loading: locationsLoading } = useInboxLocations()
  const [savedLocationId, setSavedLocationId, savedLocationLoaded] = useSettings<string>(INBOX_LOCATION_SETTING_KEY)
  // Undefined until the cold-start fetch settles; `{}` also means "no tie to break".
  const [coldStartUnreads, setColdStartUnreads] = useState<Record<string, number> | undefined>(undefined)

  useEffect(() => {
    if (!locationId) {
      setLocation(undefined)
      return
    }
    bg(async () => {
      try {
        const result = await fetchInboxLocation(locationId)
        // Clearing the param hands the choice back to the resolver below. Only on a
        // positively loaded record: a failed fetch must not eject someone mid-session.
        if (result && !hasInboxProduct(result)) {
          setLocation(undefined)
          handleSearchParams({ locationId: undefined })
          return
        }
        setLocation(result ?? undefined)
      } catch (err) {
        logError(`Error fetching inbox location`, err)
      }
    })
  }, [locationId])

  // Remember the open inbox so the next visit skips straight to it.
  useEffect(() => {
    if (!locationId || !savedLocationLoaded) return
    if (savedLocationId === locationId) return
    bg(setSavedLocationId(INBOX_LOCATION_SETTING_KEY, locationId))
  }, [locationId, savedLocationLoaded, savedLocationId])

  // A remembered location settles the choice on its own; only a real tie is
  // worth a fetch.
  const savedLocationUsable = !!savedLocationId && inboxLocations.some((loc) => loc.id === savedLocationId)
  const needsUnreadTieBreak =
    !locationId && savedLocationLoaded && !locationsLoading && !savedLocationUsable && inboxLocations.length > 1

  useEffect(() => {
    if (!needsUnreadTieBreak || coldStartUnreads !== undefined) return
    bg(async () => {
      try {
        setColdStartUnreads(await fetchUnreadThreadsByLocation())
      } catch (err) {
        logError(`Error fetching location unreads`, err)
        // Resolution must not hang on a failed count; fall through to name order.
        setColdStartUnreads({})
      }
    })
  }, [needsUnreadTieBreak, coldStartUnreads])

  // `replace` keeps the param-less URL out of history, so Back leaves the inbox
  // rather than bouncing through this redirect again.
  useEffect(() => {
    if (locationId || !savedLocationLoaded || locationsLoading) return
    if (needsUnreadTieBreak && coldStartUnreads === undefined) return
    const next = resolveInboxLocationId(inboxLocations, savedLocationId, coldStartUnreads ?? {})
    if (next) handleSearchParams({ locationId: next }, { replace: true })
  }, [
    locationId,
    savedLocationLoaded,
    savedLocationId,
    locationsLoading,
    inboxLocations,
    needsUnreadTieBreak,
    coldStartUnreads,
  ])

  const data = useMemo<InboxPageData>(
    () => ({ locationId, filter, query, threadId: tabThreadId, location }),
    [locationId, filter, query, tabThreadId, location],
  )

  const controller = useInboxController({ data, searchParams })

  // Once a location is open the controller's sync already counts unread across
  // every location, so the picker's badges ride that instead of a second poll.
  const effectiveUnreadLocations = data.locationId ? (controller.unreadLocations ?? {}) : (coldStartUnreads ?? {})

  // Per-thread AI Agent state for the sidebar list. We batch-fetch the
  // `aiEnabled` flag for every thread currently in the conversation list so the
  // <Conversation> rows that have AI activity can render the muted "AI:
  // Disabled" pill when the agent has been turned off for that specific thread.
  // The query keys on the joined list of thread ids — when the list changes
  // (new sync), we refetch once and rebuild the set.
  const conversationThreadIds = useMemo(() => {
    const ids = controller.conversations.map((c) => c.threadId).filter((id): id is string => !!id)
    // dedupe + sort for a stable cache key; cap to avoid overly long ES query strings
    return Array.from(new Set(ids)).sort().slice(0, 50)
  }, [controller.conversations])

  // useQuery subscribes to urql's normalized cache — when UPDATE_THREAD_MUTATION
  // fires and returns a Thread with updated aiEnabled, graphcache updates the
  // entity in-place and this query result reflects it immediately.
  const disabledThreadsSearch = useMemo(
    () => (conversationThreadIds.length > 0 ? `id:(${conversationThreadIds.join(' OR ')})` : ''),
    [conversationThreadIds],
  )
  const [threadsResult] = useQuery({
    query: INBOX_THREADS_QUERY,
    variables: { search: disabledThreadsSearch, pagination: { limit: conversationThreadIds.length } },
    pause: conversationThreadIds.length === 0,
  })
  const disabledThreadIds = useMemo(() => {
    const next = new Set<string>()
    for (const t of threadsResult.data?.threads?.hits ?? []) {
      if (t.aiEnabled === false) next.add(t.id)
    }
    return next
  }, [threadsResult.data])
  const threadNameOverrides = useMemo(() => {
    const map: Record<string, string> = {}
    for (const t of threadsResult.data?.threads?.hits ?? []) {
      if (t.name) map[t.id] = t.name
    }
    return map
  }, [threadsResult.data])

  const subnav = InboxSubnavs({
    conversations: controller.conversations,
    selectedThreadId: controller.threadId,
    selectedContactId: controller.contactId,
    isLoadingConversations: !controller.hasSynced,
    locations: effectiveUnreadLocations,
    disabledThreadIds,
    threadNameOverrides,
    addConversation: controller.addConversation,
    resetInboxState: controller.resetInboxState,
    selectConversation: controller.selectConversation,
    archiveConversation: controller.archiveConversation,
    markConversationUnread: controller.markConversationUnread,
    onConversationsChange: controller.setConversations,
  })
  // Desktop pushes the list into the sidebar rail. On mobile we render it in
  // `main` instead (master-detail), so keep the rail empty to avoid duplicating
  // the list inside the hamburger nav drawer.
  useSubnav(isMobile ? [] : subnav)

  return {
    data,
    searchParams,
    handleSearchParams,
    controller,
    location,
    effectiveUnreadLocations,
    inboxLocations: locationsLoading ? undefined : inboxLocations,
    isMobile,
    subnav,
  }
}
